import { helveticaWidth } from './helveticaWidth.ts';

/**
 * A parallelogram, trapezoid or triangle with its base and height labelled
 * (and a trapezoid's top). See ADR 0121 §2.
 *
 * In `Shared/Pdf/` with the other drawings, on ADR 0033 §1's argument: it
 * knows nothing about worksheets. It is given the figure's kind and lengths,
 * the labels to print (`8 cm`) and the largest it may be drawn, and draws that.
 *
 * **Every figure stands on its base**, a horizontal side at the bottom. The
 * corners (y up, base along y = 0, `h` the height) are:
 *
 *   parallelogram  (0,0) (base,0) (offset+base,h) (offset,h)
 *   trapezoid      (0,0) (base,0) (offset+top,h)  (offset,h)
 *   triangle       (0,0) (base,0) (offset,h)
 *
 * **The height is a dashed line from the top-left corner (or the apex) down to
 * the base, with a right-angle mark at its foot.** The foot is (offset, 0),
 * strictly inside the base while the generator keeps `offset` from 1 to
 * `base - 1`, so the height never needs the base extended.
 *
 * **It is drawn to scale, with one factor for both directions**, the rule 0038
 * §2 set for rectangles. The generator keeps the figure's overall width and
 * height within 4:1; like `LabelledRectangle` this does not check that, because
 * a component that silently redrew a figure it was asked for would be the very
 * failure the ADR is about.
 *
 * **The slanted sides are never labelled.** The base's label is centred under
 * it, a trapezoid's top label is centred over the top, and the height's sits
 * beside the dashed line at mid-height, on whichever side has more room.
 *
 * The drawing is a **fixed box for a given maximum size**, with the figure
 * centred in it, so rows line up down the page and the answer rule under each
 * lands at the same height. `areaFigureGeometry` is the whole of the
 * arithmetic, exported so the generator can ask whether a figure's height label
 * fits (`fits`) and a test can assert that labels clear every line without
 * reading SVG back.
 *
 * (The component, `LabelledAreaFigure.tsx`, draws this. The arithmetic lives here, with no
 * renderer import, because the generator asks `areaFigureGeometry` whether a figure fits and
 * the generator is in the main bundle, which must not carry `@react-pdf/renderer`.)
 *
 * **The dash is the Svg `strokeDasharray`,** which `@react-pdf` writes into the PDF as
 * a real `[4 3] 0 d`. It splits the value on commas only: "4 3" throws, "4,3" works.
 * `scripts/measurePages` reads a dashed stroke as one solid line (it ignores `d`),
 * so a label printed over any part of the height, gap or dash, fails the layout check.
 */

export type AreaFigureKind = 'parallelogram' | 'trapezoid' | 'triangle';

export interface LabelledAreaFigureProps {
    kind: AreaFigureKind;
    /** The bottom side's real length. Sets the drawn proportions, not the size. */
    base: number;
    /** The top parallel side's real length (a trapezoid only). */
    top?: number;
    height: number;
    /** How far right the top's left end, or the apex, sits from the base's left end. */
    offset: number;
    /** What to print: `8 cm`. */
    labels: { base: string; height: string; top?: string };
    /** The largest the figure itself (not its labels) is drawn. The same for every figure on a sheet. */
    maxWidth: number;
    maxHeight: number;
    fontSize?: number;
}

export interface Point {
    x: number;
    y: number;
}

export interface LabelBox {
    text: string;
    /** The centre of the label's box. */
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface AreaFigureGeometry {
    svgWidth: number;
    svgHeight: number;
    /** Points per unit of length, the same both ways. */
    scale: number;
    /** The drawn figure's size in points. */
    drawn: { width: number; height: number };
    /** The outline's corners in order, in Svg coordinates (y down). */
    corners: Point[];
    /** The dashed height: from the top-left corner (or apex) down to its foot on the base. */
    height: { from: Point; foot: Point };
    /** The right-angle mark at the foot, an L of three points, and its side length. */
    mark: [Point, Point, Point];
    markSide: number;
    /** Which side of the dashed line the mark and the height label are on: 1 right, -1 left. */
    markDirection: 1 | -1;
    heightDirection: 1 | -1;
    labels: { base: LabelBox; top?: LabelBox; height: LabelBox };
    /**
     * Whether the height label sits clear of the dashed line, every side and the
     * mark, with the mark big enough to read and every label inside the box
     * (ADR 0121 §4). The generator draws only figures for which this is true.
     */
    fits: boolean;
    /** The least gap between the height label and anything it must clear, in points. */
    clearance: number;
}

export const OUTLINE_WIDTH = 1.2;
export const DASH_WIDTH = 1;
/** Drawn length of a dash and of the gap after it, in points. A comma list: the renderer splits on commas only. */
export const DASH_PATTERN = '4,3';
/** The right-angle mark's side, at most; and the largest share of the height it may take. */
const MARK_SIDE = 7;
const MARK_MAX_SHARE = 0.25;
/** Smaller than this and the mark is a smudge, so the figure does not qualify. */
const MARK_MIN_SIDE = 4;
/** Clear space between the base or top label's box and the side it names. */
const LABEL_GAP = 5;
/** Between the height label's box and the dashed line it names. */
const HEIGHT_LABEL_GAP = 4;
/** The least gap the height label keeps from every line, centre of the stroke to the label's box. */
const MIN_CLEARANCE = 3;
/**
 * How far the dashed height's foot must stand from the base's left end, and for
 * a triangle from its right end too, in points at the size the figure is drawn.
 * Closer and the dashed line nearly lies along the slanted side that meets the
 * base there, and the height reads as the side. Held at the half-page size,
 * the smaller one; the full page's scale is 1.25 times as large, so it holds there.
 */
export const FOOT_CLEARANCE = 12;
/**
 * Clear space between the top of the box, where the prompt above it ends, and
 * the top of the tallest label a figure carries (a trapezoid's top label).
 * Every kind reserves it, so the boxes are one size and rows line up.
 */
export const PROMPT_GAP = 6;
/** A hair of room so a stroke at the edge of the Svg is not clipped. */
const MARGIN = 1;
/** Helvetica's digits and capitals stand about this tall, in ems. */
const CAP_HEIGHT = 0.72;
/** The widest label a figure can carry: the longest length a sheet allows, 100 cm. */
const WIDEST_LABEL = '100 cm';

/** The corners of a figure in length units, y up, counter-clockwise from the base's left end. */
export function areaFigureCorners({
    kind,
    base,
    top,
    height,
    offset,
}: Pick<LabelledAreaFigureProps, 'kind' | 'base' | 'top' | 'height' | 'offset'>): Point[] {
    if (kind === 'parallelogram') {
        return [
            { x: 0, y: 0 },
            { x: base, y: 0 },
            { x: offset + base, y: height },
            { x: offset, y: height },
        ];
    }
    if (kind === 'trapezoid') {
        return [
            { x: 0, y: 0 },
            { x: base, y: 0 },
            { x: offset + (top ?? base), y: height },
            { x: offset, y: height },
        ];
    }
    return [
        { x: 0, y: 0 },
        { x: base, y: 0 },
        { x: offset, y: height },
    ];
}

/** The room above the figure's drawing area: the prompt gap, then a label and its gap. */
const topRoom = (fontSize: number) => PROMPT_GAP + fontSize * CAP_HEIGHT + LABEL_GAP + MARGIN;

/**
 * The fixed box a figure drawn within `maxWidth` by `maxHeight` sits in: wide
 * enough for the widest label to hang half past either end, tall enough for a
 * label above (a trapezoid's top, kept clear of the prompt by `PROMPT_GAP`) and
 * below. Every kind has the room above, so the box is the same for all of them.
 */
export function areaFigureBox(
    maxWidth: number,
    maxHeight: number,
    fontSize = 10
): { width: number; height: number; side: number } {
    const side = Math.ceil(helveticaWidth(WIDEST_LABEL, fontSize) / 2) + MARGIN;
    return {
        width: maxWidth + 2 * side,
        height: topRoom(fontSize) + maxHeight + LABEL_GAP + fontSize * CAP_HEIGHT + MARGIN,
        side,
    };
}

const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

function pointToSegment(p: Point, a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length2 = dx * dx + dy * dy;
    const t = length2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / length2));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export interface Rect {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

export const rectOf = (b: LabelBox): Rect => ({
    left: b.x - b.width / 2,
    right: b.x + b.width / 2,
    top: b.y - b.height / 2,
    bottom: b.y + b.height / 2,
});

const pointToRect = (p: Point, r: Rect) =>
    Math.hypot(Math.max(r.left - p.x, 0, p.x - r.right), Math.max(r.top - p.y, 0, p.y - r.bottom));

/** The gap between a segment and a rectangle: 0 if they touch or cross. */
export function segmentToRect(a: Point, b: Point, r: Rect): number {
    const corners: Point[] = [
        { x: r.left, y: r.top },
        { x: r.right, y: r.top },
        { x: r.right, y: r.bottom },
        { x: r.left, y: r.bottom },
    ];
    if (pointToRect(a, r) === 0 || pointToRect(b, r) === 0) return 0;
    for (let i = 0; i < 4; i++) {
        const c = corners[i];
        const d = corners[(i + 1) % 4];
        if (cross(a, b, c) * cross(a, b, d) < 0 && cross(c, d, a) * cross(c, d, b) < 0) return 0;
    }
    return Math.min(pointToRect(a, r), pointToRect(b, r), ...corners.map((c) => pointToSegment(c, a, b)));
}

export function areaFigureGeometry({
    kind,
    base,
    top,
    height,
    offset,
    labels,
    maxWidth,
    maxHeight,
    fontSize = 10,
}: LabelledAreaFigureProps): AreaFigureGeometry {
    const capHeight = fontSize * CAP_HEIGHT;
    const box = areaFigureBox(maxWidth, maxHeight, fontSize);
    const textBox = (text: string) => ({ text, width: helveticaWidth(text, fontSize), height: capHeight });

    const units = areaFigureCorners({ kind, base, top, height, offset });
    const widthUnits = Math.max(1, ...units.map((p) => p.x));
    const heightUnits = Math.max(1, height);
    const scale = Math.min(maxWidth / widthUnits, maxHeight / heightUnits);
    const drawn = { width: widthUnits * scale, height: heightUnits * scale };

    // The figure is centred in its drawing area, between the label rooms above and below.
    const left = (box.width - drawn.width) / 2;
    const bottom = topRoom(fontSize) + (maxHeight + drawn.height) / 2;
    const at = (p: Point): Point => ({ x: left + p.x * scale, y: bottom - p.y * scale });
    const corners = units.map(at);

    const foot = at({ x: offset, y: 0 });
    const apex = at({ x: offset, y: height });
    // The slanted sides, as segments in Svg coordinates: every outline edge that is not level.
    const slanted: [Point, Point][] = corners
        .map((c, i) => [c, corners[(i + 1) % corners.length]] as [Point, Point])
        .filter(([a, b]) => Math.abs(a.y - b.y) > 1e-9);
    // How far inside a slanted side a point is (the outline runs counter-clockwise in y-up, so
    // clockwise on the page): positive inside.
    const inside = ([a, b]: [Point, Point], p: Point) => -cross(a, b, p) / Math.hypot(b.x - a.x, b.y - a.y);

    // How much room there is beside the dashed line at a height above the base, on each side.
    const roomAt = (direction: 1 | -1, y: number) => {
        const x = foot.x;
        const ends = slanted
            .map(([a, b]) => {
                const t = (y - a.y) / (b.y - a.y);
                return a.x + t * (b.x - a.x);
            })
            .filter((edgeX) => (direction === 1 ? edgeX > x : edgeX < x));
        if (ends.length === 0) return 0;
        return direction === 1 ? Math.min(...ends) - x : x - Math.max(...ends);
    };

    // The height label: mid-height, beside the dashed line, on the roomier side.
    const midY = (foot.y + apex.y) / 2;
    const heightText = textBox(labels.height);
    const heightDirection: 1 | -1 = roomAt(1, midY) >= roomAt(-1, midY) ? 1 : -1;
    const heightLabel: LabelBox = {
        ...heightText,
        x: foot.x + heightDirection * (HEIGHT_LABEL_GAP + heightText.width / 2),
        y: midY,
    };

    // The right-angle mark at the foot, on the side with more room at the base, shrunk
    // until its far corner is inside the figure.
    const markDirection: 1 | -1 = roomAt(1, foot.y - 0.5) >= roomAt(-1, foot.y - 0.5) ? 1 : -1;
    let markSide = Math.min(MARK_SIDE, MARK_MAX_SHARE * drawn.height, 0.5 * roomAt(markDirection, foot.y - 0.5));
    const farCorner = (s: number): Point => ({ x: foot.x + markDirection * s, y: foot.y - s });
    const markFits = (s: number) =>
        slanted.every((side) => inside(side, farCorner(s)) >= 1) &&
        slanted.every((side) => inside(side, { x: foot.x + markDirection * s, y: foot.y - 0.5 }) >= 1);
    while (markSide >= MARK_MIN_SIDE && !markFits(markSide)) {
        markSide -= 0.5;
    }
    const markVisible = markSide >= MARK_MIN_SIDE;
    markSide = Math.max(markSide, 0);
    const mark: [Point, Point, Point] = [
        { x: foot.x + markDirection * markSide, y: foot.y },
        farCorner(markSide),
        { x: foot.x, y: foot.y - markSide },
    ];

    // The base label hangs below the base, centred; a trapezoid's top label stands over the top.
    const baseText = textBox(labels.base);
    const baseLabel: LabelBox = {
        ...baseText,
        x: left + (base * scale) / 2,
        y: bottom + LABEL_GAP + baseText.height / 2,
    };
    let topLabel: LabelBox | undefined;
    if (kind === 'trapezoid' && labels.top !== undefined) {
        const topText = textBox(labels.top);
        topLabel = {
            ...topText,
            x: left + (offset + (top ?? base) / 2) * scale,
            y: apex.y - LABEL_GAP - topText.height / 2,
        };
    }

    // What the height label must clear: the dashed line, every side, and the mark.
    const lines: [Point, Point][] = [
        [apex, foot],
        ...corners.map((c, i) => [c, corners[(i + 1) % corners.length]] as [Point, Point]),
        [mark[0], mark[1]],
        [mark[1], mark[2]],
    ];
    const heightRect = rectOf(heightLabel);
    const clearance = Math.min(...lines.map(([a, b]) => segmentToRect(a, b, heightRect)));
    const labelsInBox = [heightLabel, baseLabel, ...(topLabel ? [topLabel] : [])].every((label) => {
        const r = rectOf(label);
        return r.left >= 0 && r.top >= 0 && r.right <= box.width && r.bottom <= box.height;
    });
    const labelInside = slanted.every((side) => inside(side, heightLabel) > 0);
    // The dashed line stands clear of the sides that meet the base either side of its foot.
    const footLeft = foot.x - corners[0].x;
    const footRight = corners[1].x - foot.x;
    const footClear = footLeft >= FOOT_CLEARANCE && (kind !== 'triangle' || footRight >= FOOT_CLEARANCE);
    const fits = clearance >= MIN_CLEARANCE && markVisible && labelsInBox && labelInside && footClear;

    return {
        svgWidth: box.width,
        svgHeight: box.height,
        scale,
        drawn,
        corners,
        height: { from: apex, foot },
        mark,
        markSide,
        markDirection,
        heightDirection,
        labels: { base: baseLabel, top: topLabel, height: heightLabel },
        fits,
        clearance,
    };
}
